export const TAX_THRESHOLD = 2000000;
export const TAX_RATE_NPWP = 1.5;
export const TAX_RATE_NON_NPWP = 3;

const toOptionalNumber = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string" && value.trim() === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumber = (value, fallback = 0) => {
  const parsed = toOptionalNumber(value);
  return parsed === null ? fallback : parsed;
};

const roundHalfToEven = (value) => {
  const num = toNumber(value, 0);
  const sign = num < 0 ? -1 : 1;
  const abs = Math.abs(num);
  const floor = Math.floor(abs);
  const diff = abs - floor;

  if (diff > 0.5) {
    return sign * (floor + 1);
  }
  if (diff < 0.5) {
    return sign * floor;
  }
  return sign * (floor % 2 === 0 ? floor : floor + 1);
};

export const roundCurrency = (value) => roundHalfToEven(value);

export const hasSupplierNpwp = (supplier) => String(supplier?.nik_npwp || "").trim() !== "";

export const getItemQtyInput = (item) => {
  const qtyInput = toOptionalNumber(item?.qty_input);
  if (qtyInput !== null) {
    return qtyInput;
  }
  const qty = toOptionalNumber(item?.qty);
  return qty === null ? 0 : qty;
};

export const getItemSubtotalBeforeTax = (item) => {
  const subtotalInput = toOptionalNumber(item?.subtotal_input);
  if (subtotalInput !== null) {
    return subtotalInput;
  }
  const subtotal = toOptionalNumber(item?.subtotal);
  return subtotal === null ? 0 : subtotal;
};

export const calculateTaxBreakdown = (subtotalBeforeTax, supplierHasNpwp) => {
  const subtotalSebelumPajak = Math.max(toNumber(subtotalBeforeTax, 0), 0);
  const tarifPajakPersen =
    subtotalSebelumPajak > TAX_THRESHOLD
      ? supplierHasNpwp
        ? TAX_RATE_NPWP
        : TAX_RATE_NON_NPWP
      : 0;

  const nilaiPajak =
    tarifPajakPersen > 0
      ? roundCurrency(subtotalSebelumPajak * (tarifPajakPersen / 100))
      : 0;

  return {
    subtotalSebelumPajak,
    tarifPajakPersen,
    nilaiPajak,
    totalBelanja: subtotalSebelumPajak + nilaiPajak,
  };
};

export const distributeTaxBySubtotals = (subtotals, totalTax) => {
  if (!Array.isArray(subtotals) || subtotals.length === 0) {
    return [];
  }

  const totalTaxValue = Math.max(roundCurrency(totalTax), 0);
  const totalSubtotal = subtotals.reduce((sum, subtotal) => sum + toNumber(subtotal, 0), 0);
  if (totalTaxValue <= 0 || totalSubtotal <= 0) {
    return subtotals.map(() => 0);
  }

  const distributed = [];
  let runningTotal = 0;
  const lastIndex = subtotals.length - 1;

  subtotals.forEach((subtotal, idx) => {
    if (idx === lastIndex) {
      distributed.push(Math.max(totalTaxValue - runningTotal, 0));
      return;
    }

    let share = roundCurrency((toNumber(subtotal, 0) / totalSubtotal) * totalTaxValue);
    const remainingTax = Math.max(totalTaxValue - runningTotal, 0);
    share = Math.min(Math.max(share, 0), remainingTax);
    runningTotal += share;
    distributed.push(share);
  });

  return distributed;
};

export const buildItemsWithAppliedTax = (items, taxBreakdown) => {
  const safeItems = Array.isArray(items) ? items : [];
  const baseSubtotals = safeItems.map((item) => getItemSubtotalBeforeTax(item));
  const distributedTax = distributeTaxBySubtotals(baseSubtotals, taxBreakdown?.nilaiPajak || 0);

  return safeItems.map((item, idx) => {
    const subtotalSebelumPajak = toNumber(baseSubtotals[idx], 0);
    const nilaiPajakItem = toNumber(distributedTax[idx], 0);
    const subtotalSetelahPajak = subtotalSebelumPajak + nilaiPajakItem;
    const qtyInput = getItemQtyInput(item);
    const hargaSatuanInput = toNumber(item?.harga_satuan_input, toNumber(item?.harga_satuan, 0));
    const hargaSatuanSetelahPajak =
      qtyInput > 0 ? roundCurrency(subtotalSetelahPajak / qtyInput) : roundCurrency(hargaSatuanInput);
    const subtotalSetelahPajakRounded =
      qtyInput > 0
        ? roundCurrency(qtyInput * hargaSatuanSetelahPajak)
        : roundCurrency(subtotalSetelahPajak);

    return {
      ...item,
      display_harga_satuan: hargaSatuanSetelahPajak,
      display_subtotal: subtotalSetelahPajakRounded,
      display_subtotal_sebelum_pajak: subtotalSebelumPajak,
      display_nilai_pajak: Math.max(subtotalSetelahPajakRounded - subtotalSebelumPajak, 0),
    };
  });
};

export const getAppliedTaxDisplaySummary = (items, taxBreakdown) => {
  const displayItems = buildItemsWithAppliedTax(items, taxBreakdown);
  const subtotalSebelumPajak = displayItems.reduce(
    (sum, item) => sum + toNumber(item.display_subtotal_sebelum_pajak, 0),
    0
  );
  const totalBelanja = displayItems.reduce((sum, item) => sum + toNumber(item.display_subtotal, 0), 0);
  const nilaiPajak = Math.max(totalBelanja - subtotalSebelumPajak, 0);

  return {
    displayItems,
    subtotalSebelumPajak,
    nilaiPajak,
    totalBelanja,
    tarifPajakPersen: toNumber(taxBreakdown?.tarifPajakPersen, 0),
  };
};

export const getTaxBreakdownFromTransaksi = (transaksi, supplierHasNpwp) => {
  const subtotalFromItems = (Array.isArray(transaksi?.items) ? transaksi.items : []).reduce(
    (sum, item) => sum + getItemSubtotalBeforeTax(item),
    0
  );
  const subtotalFromData = toOptionalNumber(transaksi?.subtotal_sebelum_pajak);
  const subtotalSebelumPajakRaw =
    subtotalFromData !== null && (subtotalFromData > 0 || subtotalFromItems <= 0)
      ? subtotalFromData
      : subtotalFromItems;
  const subtotalSebelumPajak = Math.max(subtotalSebelumPajakRaw, 0);

  const fallbackBreakdown = calculateTaxBreakdown(subtotalSebelumPajak, supplierHasNpwp);

  const totalFromData = toOptionalNumber(transaksi?.total_belanja);
  const totalBelanja =
    totalFromData !== null && (totalFromData > 0 || fallbackBreakdown.totalBelanja <= 0)
      ? totalFromData
      : fallbackBreakdown.totalBelanja;

  const calculatedNilaiPajak = Math.max(totalBelanja - subtotalSebelumPajak, 0);
  const nilaiPajakFromData = toOptionalNumber(transaksi?.nilai_pajak);
  const nilaiPajak =
    nilaiPajakFromData !== null && (nilaiPajakFromData > 0 || calculatedNilaiPajak <= 0)
      ? nilaiPajakFromData
      : calculatedNilaiPajak;

  const calculatedTarifPajakPersen =
    subtotalSebelumPajak > 0 && nilaiPajak > 0
      ? (nilaiPajak / subtotalSebelumPajak) * 100
      : 0;
  const tarifPajakFromData = toOptionalNumber(transaksi?.tarif_pajak_persen);
  const tarifPajakPersen =
    tarifPajakFromData !== null && (tarifPajakFromData > 0 || calculatedTarifPajakPersen <= 0)
      ? tarifPajakFromData
      : calculatedTarifPajakPersen;

  return {
    subtotalSebelumPajak,
    tarifPajakPersen,
    nilaiPajak,
    totalBelanja,
  };
};
